import BottomSheet, {
  BottomSheetFlatList,
  BottomSheetFlatListMethods,
} from '@gorhom/bottom-sheet';
import i18n from '@/utils/i18n';
import NavigationCenterIcon from '@assets/icons/navigation-center.svg';
import React, { useCallback } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { ApolloError } from '@apollo/client';
import { Business } from '@/components/locations/types';
import { BusinessInfoCard } from './business-info-card';
import { Button } from '@/components/button';
import { Coordinate } from '@usepepper/ui/molecules/map/types';
import { createStyles } from '@/contexts/theme-context';
import { shadow, TYPOGRAPHY } from '@/styles';
import { Text, TouchableOpacity, View } from 'react-native';
import { toPercentageString } from '@/utils/percentage';

interface Props {
  bottomSheetRef: React.RefObject<BottomSheet | null>;
  collapsedContainerHeight: number;
  createAccount: (business: Business) => void;
  dragLeadGeneratorLocation: Coordinate | null;
  error?: ApolloError;
  fetchingMore: boolean;
  fetchMore: () => void;
  flatListRef: React.RefObject<BottomSheetFlatListMethods | null>;
  isCollapsed: boolean;
  isUserLocation: boolean;
  isVisible: boolean;
  loading: boolean;
  navigateToProspect: (uuid: string, restaurantName: string) => void;
  nearbyBusinesses?: Array<Business>;
  newAccountYelpBusinessID: string | null;
  onPositionChanged: (index: number) => void;
  openContainerHeight: number;
  retry: () => void;
  selectCurrentLocation: () => void;
  snapPoints: string[];
  supplierCountryCode?: string;
}

export const BusinessInfoCardList: React.FC<Props> = ({
  createAccount,
  collapsedContainerHeight,
  dragLeadGeneratorLocation,
  error,
  fetchingMore,
  fetchMore,
  isCollapsed,
  isUserLocation,
  isVisible,
  bottomSheetRef,
  flatListRef,
  loading,
  navigateToProspect,
  nearbyBusinesses,
  newAccountYelpBusinessID,
  onPositionChanged,
  openContainerHeight,
  retry,
  selectCurrentLocation,
  snapPoints,
  supplierCountryCode,
}) => {
  const [styles] = useStyles();

  const onEndReached = () => {
    // While the list is still empty, we'll trigger an
    // onEndReached because... well we're at the end
    if (!loading) {
      fetchMore();
    }
  };

  // This function is provided to the onScrollToIndexFailed prop of the FlatList
  // component. It will be called when scrollToIndex fails to scroll to the
  // requested index. The function will scroll to the highest measured frame
  // index, then scroll to the requested index after a short delay.
  const onScrollToIndexFailed = useCallback(
    (info: {
      averageItemLength: number;
      highestMeasuredFrameIndex: number;
      index: number;
    }) => {
      flatListRef?.current?.scrollToIndex({
        animated: true,
        index: info.highestMeasuredFrameIndex,
      });
      setTimeout(() => {
        flatListRef?.current?.scrollToIndex({
          animated: true,
          index: info.index,
        });
      }, 100);
    },
    [flatListRef],
  );

  // list components
  const handleComponent = () => (
    <>
      {!isUserLocation || dragLeadGeneratorLocation ? (
        <Button
          Icon={NavigationCenterIcon}
          iconProps={{
            height: 16,
            style: styles.currentLocationIcon,
            width: 16,
          }}
          onPress={selectCurrentLocation}
          style={styles.currentLocationButton}
          trackingData={null}
        />
      ) : null}
      <View style={styles.bottomSheetHandle} />
    </>
  );

  const renderListItem = (business: Business) => (
    <BusinessInfoCard
      business={business}
      containerStyle={styles.container}
      createAccount={createAccount}
      navigateToProspect={navigateToProspect}
      newAccountYelpBusinessID={newAccountYelpBusinessID}
      supplierCountryCode={supplierCountryCode}
    />
  );
  const listFooterComponent = () =>
    loading || fetchingMore ? (
      <View style={styles.loading}>
        <ActivityIndicator />
      </View>
    ) : null;

  return (
    <>
      {isVisible ? (
        <BottomSheet
          handleComponent={handleComponent}
          index={0}
          onChange={onPositionChanged}
          ref={bottomSheetRef}
          snapPoints={snapPoints}
        >
          {isCollapsed ? (
            <TouchableOpacity
              onPress={() => onPositionChanged(1)}
              style={[
                styles.collapsedStateContainer,
                { height: toPercentageString(collapsedContainerHeight) },
              ]}
            >
              <Text style={styles.collapsedText}>{i18n.t('view_list')}</Text>
            </TouchableOpacity>
          ) : nearbyBusinesses?.length !== 0 ? (
            <BottomSheetFlatList
              data={nearbyBusinesses}
              keyExtractor={(i) => i.yelp_business_id}
              ListFooterComponent={listFooterComponent}
              onEndReached={onEndReached}
              onScrollToIndexFailed={onScrollToIndexFailed}
              ref={flatListRef}
              renderItem={({ item: business }) => renderListItem(business)}
              scrollIndicatorInsets={{ right: 1 }}
              style={styles.bottomSheetList}
            />
          ) : error ? (
            <View
              style={[
                styles.errorStateContainer,
                { height: toPercentageString(openContainerHeight) },
              ]}
            >
              <Text style={styles.errorText}>
                {i18n.t('lead_generator.error')}
              </Text>
              <Button
                onPress={retry}
                style={styles.errorButton}
                trackingData={{
                  name: 'lead_generator.retry',
                }}
              >
                {i18n.t('retry')}
              </Button>
            </View>
          ) : (
            <View style={styles.errorStateContainer}>
              <Text style={styles.errorText}>
                {i18n.t('no_businesses_found_nearby')}
              </Text>
            </View>
          )}
        </BottomSheet>
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  bottomSheetHandle: {
    alignSelf: 'center',
    backgroundColor: theme.colors.stroke,
    borderRadius: 3,
    height: 6,
    marginVertical: 8,
    width: 100,
  },
  bottomSheetList: {
    paddingHorizontal: 24,
  },
  collapsedStateContainer: {
    backgroundColor: theme.colors.white,
    flex: 0,
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingTop: 8,
    textAlign: 'center',
  },
  collapsedText: {
    ...TYPOGRAPHY.h3,
    alignSelf: 'center',
    marginBottom: 24,
  },
  container: {
    borderBottomColor: theme.colors.stroke,
    borderBottomWidth: 1,
    marginBottom: 16,
    paddingBottom: 16,
  },
  currentLocationButton: {
    ...shadow({
      color: theme.colors.primary,
      elevation: 5,
      opacity: 0.05,
      radius: 24,
      x: 0,
      y: 4,
    }),
    backgroundColor: theme.colors.white,
    borderRadius: 24,
    borderWidth: 0,
    height: 48,
    position: 'absolute',
    right: 16,
    top: -64,
    width: 48,
  },
  currentLocationIcon: {
    margin: 16,
  },
  errorButton: {
    alignSelf: 'center',
  },
  errorStateContainer: {
    backgroundColor: theme.colors.white,
    flex: 0,
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingTop: 24,
    textAlign: 'center',
  },
  errorText: {
    ...TYPOGRAPHY.h4,
    alignSelf: 'center',
    marginBottom: 24,
  },
  loading: {
    flex: 1,
    paddingBottom: 64,
    paddingTop: 32,
  },
}));
